import react, {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  type ChartData,
  type ScatterDataPoint,
  type ChartDataset,
} from "chart.js";
import clsx from "clsx";
import { isNil } from "lodash";
import moment from "moment";
import { Bar } from "react-chartjs-2";

import { gql } from "@apollo/client";
import { LoadingSlide, LoadingSlideNoText } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  CustomPaging,
  DataTypeProvider,
  PagingState,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  PagingPanel,
  TableColumnResizing,
  TableFixedColumns,
  VirtualTable,
  TableHeaderRow,
} from "@devexpress/dx-react-grid-bootstrap4";
import { SalesPageV3Provider, usePageConfiguration } from "@features/sales-v3";
import { SalesV3Provider } from "@features/sales-v3/table/contexts";
import { DateFilters } from "@features/sales-v3/top-level-filters/components/date-filters";
import { useNorthbeamQuery } from "@hooks/http";
import {
  NAVBAR_QUERY,
  type PixelTrackedOrders,
  type Navbar as NavbarQuery,
  type PixelTrackedOrdersVariables,
  type PixelTrackedOrdersOverTime,
  type PixelTrackedOrdersOverTimeVariables,
} from "@north-beam/nb-client-side";
import { makeOverviewRows } from "@north-beam/nb-client-side/metrics";
import { formatting } from "@north-beam/nb-common";
import { formatDateTime } from "@north-beam/nb-common/dates";
import { SingleBadgeDataFormatter } from "@pages/orders/orders-table/formatters";
import { SecondaryButton } from "@shared/buttons";
import { Select } from "@shared/selects";
import { TextInput } from "@shared/text-input";
import { downloadGenericCsv } from "@utils/csv";

import { type OrdersApiStepMetadata } from "../orders-api/step-metadata";

import { MetricCard } from "./metric-card";

interface Props {
  stepMetadata?: OrdersApiStepMetadata;
  updateStepMetadata?: (data: OrdersApiStepMetadata) => void;
}

export const PixelValidation = (props: Props) => {
  const { data, loading } = useNorthbeamQuery<NavbarQuery>(NAVBAR_QUERY, {
    doNotRaiseErrorOnFailure: true,
  });

  if (loading) {
    return (
      <div className="mt-12">
        <div className="text-2xl font-semibold">Verify your Pixel Tracking</div>
        <LoadingSlide />
      </div>
    );
  }

  if (!data?.me.datasetExists) {
    return (
      <div className="mt-12">
        <div className="text-2xl font-semibold">Verify your Pixel Tracking</div>
        <div className="my-4">
          Your attribution data is not ready yet. Check in a few hours.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-12">
      <div className="text-2xl font-semibold mb-5">
        Verify your Pixel Tracking
      </div>
      <SalesV3Provider>
        <SalesPageV3Provider defaultAccountingMode="cash">
          <PixelValidationContents />
        </SalesPageV3Provider>
      </SalesV3Provider>
    </div>
  );
};

function PixelValidationContents() {
  return (
    <div>
      <PixelValidationChart />
      <PixelValidationTable />
    </div>
  );
}

const ListDataFormatter = ({ value }: DataTypeProvider.ValueFormatterProps) => {
  return <div>{value?.join(", ")}</div>;
};

const DateDataFormatter = ({ value }: DataTypeProvider.ValueFormatterProps) => {
  return (
    <span className="">{formatDateTime(moment(value).toISOString())}</span>
  );
};

const DollarDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  const { user } = useUser();
  return (
    <span className="">
      {formatting.formatCurrency(value, {
        precision: "exact",
        code: user.currencies.currentCurrency,
      })}
    </span>
  );
};

const CustomerStageDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  return (
    <div>
      {{
        first_time: "First Time",
        returning: "Returning",
      }[value as string] ?? "Unknown"}
    </div>
  );
};

const YesIsGoodYesNoDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  return (
    <SingleBadgeDataFormatter
      value={value ? "Yes" : "No"}
      shouldAnon={false}
      type="yes-no"
      className={clsx({
        "bg-alien-2 hover:bg-alien-2 text-alien+2": value,
        "bg-mars-2 hover:bg-mars-2 text-mars+2": !value,
      })}
    />
  );
};

const YesIsBadYesNoDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  return (
    <SingleBadgeDataFormatter
      value={value ? "Yes" : "No"}
      shouldAnon={false}
      type="yes-no"
      className={clsx({
        "bg-alien-2 hover:bg-alien-2 text-alien+2": !value,
        "bg-mars-2 hover:bg-mars-2 text-mars+2": value,
      })}
    />
  );
};

const StatusTrackingDataFormatter = ({
  value,
}: DataTypeProvider.ValueFormatterProps) => {
  return (
    <div className="w-full flex justify-center text-md">
      <div>
        {
          {
            success: "🟢",
            danger: "🔴",
            warning: "🟡",
          }[value as string]
        }
      </div>
    </div>
  );
};

export const TableHeaderContent = (
  props: TableHeaderRow.ContentProps & {
    column: { name: string; title: string; tooltip?: string };
  },
) => (
  <TableHeaderRow.Content {...props}>
    <GenericTooltip content={props.column.tooltip}>
      {props.column.title}
    </GenericTooltip>
  </TableHeaderRow.Content>
);

const determineTrackingStatus = ({
  isPixelTracked,
  isPixelTrackable,
  isServerTracked,
}: {
  isPixelTracked: boolean;
  isPixelTrackable: boolean;
  isServerTracked: boolean;
}): {
  statusStoplight: "danger" | "warning" | "success";
  description: string;
} => {
  if (isPixelTracked && !isServerTracked && isPixelTrackable) {
    return {
      statusStoplight: "danger",
      description: "Server data is missing",
    };
  }

  if (isPixelTracked && !isServerTracked && !isPixelTrackable) {
    return {
      statusStoplight: "danger",
      description: "Contact support to validate this",
    };
  }

  if (!isPixelTracked && isServerTracked && isPixelTrackable) {
    return {
      statusStoplight: "warning",
      description: "Pixel data is missing",
    };
  }

  if (!isPixelTracked && isServerTracked && !isPixelTrackable) {
    return {
      statusStoplight: "warning",
      description: "Not trackable, check order tags to validate this",
    };
  }

  // Success state
  return {
    statusStoplight: "success",
    description: "Fully tracked",
  };
};

function PixelValidationTable() {
  const { dateRangeInterval } = usePageConfiguration();
  const { user } = useUser();

  const [columns] = useState([
    {
      name: "orderId",
      title: "Order ID",
      width: 150,
      tooltip: "ID of the Order",
    },
    {
      name: "trackingStatus",
      title: "Tracking Status",
      width: 100,
      tooltip: "Tracking status of the order",
    },
    {
      name: "description",
      title: "Description",
      width: 150,
      tooltip: "Description of how the order is tracking",
    },
    {
      name: "isPixelTracked",
      title: "Pixel Confirmed",
      width: 100,
      tooltip: "If the order has both had a web and server event tracked",
    },
    {
      name: "isServerTracked",
      title: "Server Confirmed",
      width: 100,
      tooltip:
        "If the order has a server event tracked (ie from Shopify or Orders API)",
    },
    {
      name: "isPixelTrackable",
      title: "Trackable",
      width: 100,
      tooltip: "Whether or not the order should be tracked by a web pixel",
    },

    {
      name: "orderNumber",
      title: "Order Number",
      width: 120,
      tooltip: "Order number of the order",
    },
    {
      name: "purchasedAt",
      title: "Purchased At",
      width: 200,
      tooltip: "When the order was created",
    },
    {
      name: "customerStage",
      title: "Customer Type",
      width: 130,
      tooltip: "Customer type of the order",
    },
    {
      name: "platform",
      title: "Platform",
      width: 110,
      tooltip:
        "Platform the order was placed on (ie Shopify, Orders API, Amazon, etc)",
    },
    {
      name: "sourceName",
      title: "Source name",
      width: 120,
      tooltip: "Name of the source for Shopify",
    },
    {
      name: "orderTags",
      title: "Order Tags",
      width: 200,
      tooltip: "Tags that are attached to the order",
    },
    {
      name: "revenue",
      title: "Revenue",
      width: 100,
      tooltip: "Revenue of the order",
    },
  ]);

  const [isPixelOnly, setPixelOnly] = useState(false);
  const [isPixelTrackable, setIsPixelTrackable] = useState<boolean>();
  const [isPixelTracked, setIsPixelTracked] = useState<boolean>();
  const [trackingStatusFilter, setTrackingStatusFilter] = useState<
    "danger" | "warning" | "success" | null
  >(null);

  const [orderIdFilter, setOrderIdFilter] = useState<string>();

  const [rows, setRows] = useState<
    (PixelTrackedOrders["me"]["pixelTrackingEvents"]["pixelTrackedOrders"]["data"]["rows"][number] & {
      isPixelOnly: boolean;
      trackingStatus: "danger" | "warning" | "success";
      description: string;
      sourceName: string;
    })[]
  >([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSizes] = useState([1_000, 10_000, 0]);

  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastQuery, setLastQuery] = useState<string>();

  const getQueryString = useCallback(
    () =>
      `?limit=${pageSize}&offset=${pageSize * currentPage}&dateRangeStart=${
        dateRangeInterval.startDate
      }&dateRangeEnd=${dateRangeInterval.endDate}&filters=${JSON.stringify({
        isPixelOnly: isPixelOnly,
        isPixelTrackable: isPixelTrackable,
        isPixelTracked: isPixelTracked,
        orderId: orderIdFilter,
      })}`,
    [
      currentPage,
      dateRangeInterval.endDate,
      dateRangeInterval.startDate,
      isPixelOnly,
      isPixelTrackable,
      isPixelTracked,
      orderIdFilter,
      pageSize,
    ],
  );

  const loadData = useCallback(() => {
    const queryString = getQueryString();
    if (queryString !== lastQuery && !loading) {
      setLoading(true);
      user.apolloClient
        .query<PixelTrackedOrders, PixelTrackedOrdersVariables>({
          query: PIXEL_TRACKED_ORDERS_QUERY,
          variables: {
            orderTrackingFilters: {
              isPixelOnly: isPixelOnly,
              isPixelTrackable: isPixelTrackable,
              isPixelTracked: isPixelTracked,
              limit: pageSize,
              offset: (pageSize * currentPage) as number,
              orderId: orderIdFilter,
            },
            generalFilters: {
              dateRange: {
                start: dateRangeInterval.startDate,
                end: dateRangeInterval.endDate,
              },
            },
          },
        })
        .then(
          ({
            data: {
              me: {
                pixelTrackingEvents: {
                  pixelTrackedOrders: {
                    data: { rows: newRows, totalCount: newTotalCount },
                  },
                },
              },
            },
          }) => {
            setRows(
              newRows.map((i) => {
                const { description, statusStoplight } =
                  determineTrackingStatus({
                    isPixelTracked: i.isPixelTracked,
                    isServerTracked: i.isServerTracked,
                    isPixelTrackable: i.isPixelTrackable,
                  });
                return {
                  ...i,
                  isPixelOnly: i.platform === "pixel only",
                  description,
                  trackingStatus: statusStoplight,
                  sourceName:
                    i.orderTags
                      ?.find((tag) =>
                        tag.startsWith("northbeam:shopify:source_name:"),
                      )
                      ?.split(":")?.[3] || "",
                };
              }),
            );
            setTotalCount(newTotalCount);
            setLoading(false);
          },
        )
        .catch(() => setLoading(false));
      setLastQuery(queryString);
    }
  }, [
    currentPage,
    dateRangeInterval.endDate,
    dateRangeInterval.startDate,
    getQueryString,
    isPixelOnly,
    isPixelTrackable,
    isPixelTracked,
    lastQuery,
    loading,
    orderIdFilter,
    pageSize,
    user.apolloClient,
  ]);

  useEffect(() => loadData(), [loadData]);

  const exportToCsv = useCallback(() => {
    const parsedRows = rows.map(
      ({
        __typename,
        customerTags,
        subscriptionStage,
        exclusionReason,
        isTrackable,
        isPixelOnly,
        isAttributed,
        ...row
      }) => ({
        ...row,
        customerStage: row.customerStage || "",

        orderTags: row.orderTags?.join(" | ") || "",
        isPixelTracked: row.isPixelTracked ? "Yes" : "No",
        isPixelTrackable: row.isPixelTrackable ? "Yes" : "No",
        isServerTracked: row.isServerTracked ? "Yes" : "No",
        sourceName: row.sourceName || "",
        // isTrackable: row.isTrackable ? "Yes" : "No",
        // isPixelOnly: row.isPixelOnly ? "Yes" : "No",
        // isAttributed: row.isAttributed ? "Yes" : "No",
      }),
    );
    const csvColumns = columns.map((column) => {
      return {
        key: column.name,
        label: column.title,
      };
    });
    downloadGenericCsv({
      headers: csvColumns,
      data: parsedRows,
      fileName: `pixel-validation${getQueryString()}.csv`,
    });
  }, [columns, getQueryString, rows]);

  return (
    <div className="flex gap-x-5 w-full mt-5">
      <div className="shrink-0 w-[320px]  p-4 bg-cosmic-5 rounded-lg border border-gray-0">
        <a
          href="https://docs.northbeam.io/docs/guide-to-troubleshooting-pixel-tracking#step-2-check-order-tracking-status"
          target="_blank"
          className="basis-0 text-md font-semibold  "
          rel="noreferrer"
        >
          Step 2: Check Order Tracking Status
        </a>
        <div>
          <div className="text-sm mb-2 mt-3 font-semibold">
            How to Identify Issues:
          </div>
          <ul className="text-xs space-y-1 list-decimal ml-3 mb-0 font-normal">
            <li>
              <span className="font-semibold">Use the UI:</span> Work directly
              in the tool to filter and analyze data.
            </li>
            <li>
              <span className="font-semibold">Export Data via CSV:</span> Easier
              to review larger datasets.
            </li>
            <li>
              <span className="font-semibold">
                Pay attention to the color-coded tracking statuses:
              </span>{" "}
              Easier to review larger datasets.
              <ul className="space-y-1  ml-3 mt-2">
                <li>
                  🔴 Red: Server data missing (requires immediate attention).{" "}
                </li>
                <li>
                  🟡 Yellow: Pixel data missing (may indicate tracking issues).
                </li>
                <li>🟢 Green: Fully tracked (no action needed).</li>
              </ul>
            </li>
          </ul>
        </div>
        <div className="text-sm mb-2 mt-2 font-semibold">
          Understanding Tracking Indicators:
        </div>
        <ul className="text-xs space-y-1 list-decimal ml-3 mb-0 font-normal">
          <li>
            <a
              target="_blank"
              rel="noreferrer"
              href="https://docs.northbeam.io/docs/guide-to-troubleshooting-pixel-tracking#-server-data-missing-shopify-orders"
            >
              Server Data Missing (Shopify Orders)
            </a>
            <ul className="space-y-1 list-[lower-roman] ml-3 mt-2">
              <li>Backfill in Progress</li>
              <li>Shopify Connection Broken</li>
              <li>Incorrect Shopify Store Connected</li>
            </ul>
          </li>
          <li>
            <a
              target="_blank"
              rel="noreferrer"
              href="https://docs.northbeam.io/docs/guide-to-troubleshooting-pixel-tracking#-server-data-missing-non-shopify-orders"
            >
              Server Data Missing (Non-Shopify Orders)
            </a>
            <ul className="space-y-1 list-[lower-roman] ml-3 mt-2">
              <li>Order Not Received</li>
              <li>Order ID Mismatch</li>
            </ul>
          </li>
          <li>
            <a
              target="_blank"
              rel="noreferrer"
              href="https://docs.northbeam.io/docs/guide-to-troubleshooting-pixel-tracking#%F0%9F%9F%A1-pixel-data-missing"
            >
              Pixel Data Missing
            </a>
            <ul className="space-y-1 list-[lower-roman] ml-3 mt-2">
              <li>Pixel Event Not Fired</li>
              <li>Order ID Mismatch </li>
            </ul>
          </li>
          <li>
            <a
              target="_blank"
              rel="noreferrer"
              href="https://docs.northbeam.io/docs/guide-to-troubleshooting-pixel-tracking#%F0%9F%9F%A1-not-trackable"
            >
              Not Trackable
            </a>
            <ul className="space-y-1 list-[lower-roman] ml-3 mt-2">
              <li>
                Orders are classified as{" "}
                <span className="font-semibold">Not Trackable</span> if they
                cannot fire client-side pixels.
              </li>
            </ul>
          </li>
          <li>
            <a
              target="_blank"
              rel="noreferrer"
              href="https://docs.northbeam.io/docs/guide-to-troubleshooting-pixel-tracking#%F0%9F%9F%A2-fully-tracked"
            >
              Fully Tracked
            </a>
            <ul className="space-y-1 list-[lower-roman] ml-3 mt-2">
              <li>
                Nothing to do here 🎉. Both pixel and server data is confirmed
                and tracking well
              </li>
            </ul>
          </li>
        </ul>
      </div>
      <div className="w-[calc(100%-320px)] mr-5">
        <div className="flex mb-2 gap-x-2">
          <TextInput
            id="order-id-pixel-validation-input"
            placeholder="Filter order ID..."
            label="Order ID"
            className="w-[20rem]"
            value={orderIdFilter}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setOrderIdFilter(event.target.value);
              setCurrentPage(0);
            }}
          />
          <Select
            label="Tracking Status"
            width="100px"
            placeholder="Server Confirmed"
            value={trackingStatusFilter}
            onChange={(e: "danger" | "warning" | "success" | null) => {
              if (e === "danger") {
                setPixelOnly(true);
                setIsPixelTrackable(undefined);
                setIsPixelTracked(undefined);
                setCurrentPage(0);
                setTrackingStatusFilter(e);
                return;
              }
              if (e === "warning") {
                setPixelOnly(false);
                setIsPixelTrackable(undefined);
                setIsPixelTracked(false);
                setCurrentPage(0);
                setTrackingStatusFilter(e);
                return;
              }

              setPixelOnly(false);
              setIsPixelTrackable(undefined);
              setIsPixelTracked(undefined);
              setCurrentPage(0);
              setTrackingStatusFilter(e);
            }}
            options={[
              { label: "None", value: null },
              { label: "🔴", value: "danger" },
              { label: "🟡", value: "warning" },
              { label: "🟢", value: "success" },
            ]}
          />
          <Select
            label="Server Confirmed"
            width="100px"
            placeholder="Server Confirmed"
            value={isPixelOnly}
            onChange={(e) => {
              setPixelOnly(e);
              setCurrentPage(0);
              setTrackingStatusFilter(null);
            }}
            options={[
              { label: "Yes", value: false },
              { label: "No", value: true },
            ]}
          />
          <Select
            label="Pixel Confirmed"
            width="100px"
            placeholder="Pixel Confrimed"
            value={isPixelTracked}
            onChange={(e) => {
              setIsPixelTracked(e);
              setCurrentPage(0);
              setTrackingStatusFilter(null);
            }}
            options={[
              { label: "All", value: undefined },
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
          />
          <Select
            label="Pixel Trackable"
            width="100px"
            placeholder="Pixel Trackable"
            value={isPixelTrackable}
            onChange={(e) => {
              setIsPixelTrackable(e);
              setCurrentPage(0);
              setTrackingStatusFilter(null);
            }}
            options={[
              { label: "All", value: undefined },
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
          />
        </div>
        <div className="mb-2 flex">
          <DateFilters />
          <div className="self-center ml-auto ">
            <span className="font-semibold">
              Total Order Count:{" "}
              {formatting.formatInteger(totalCount, { precision: "exact" })}
            </span>
            <span className="ml-2">
              <SecondaryButton onClick={exportToCsv}>
                EXPORT CURRENT VIEW
              </SecondaryButton>
            </span>
          </div>
        </div>
        <div className="text-xs  border rounded-md relative w-full mr-5">
          <Grid rows={rows} columns={columns}>
            <PagingState
              currentPage={currentPage}
              onCurrentPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setCurrentPage(0);
              }}
            />
            <CustomPaging totalCount={totalCount} />
            <DataTypeProvider
              for={["revenue"]}
              formatterComponent={DollarDataFormatter}
            />
            <DataTypeProvider
              for={["purchasedAt"]}
              formatterComponent={DateDataFormatter}
            />
            <DataTypeProvider
              for={["orderTags"]}
              formatterComponent={ListDataFormatter}
            />
            <DataTypeProvider
              for={["customerStage"]}
              formatterComponent={CustomerStageDataFormatter}
            />
            <DataTypeProvider
              for={["isPixelOnly"]}
              formatterComponent={YesIsBadYesNoDataFormatter}
            />
            <DataTypeProvider
              for={["trackingStatus"]}
              formatterComponent={StatusTrackingDataFormatter}
            />
            <DataTypeProvider
              for={[
                // "isAttributed",
                // "isTrackable",
                "isPixelTracked",
                "isPixelTrackable",
                "isServerTracked",
              ]}
              formatterComponent={YesIsGoodYesNoDataFormatter}
            />
            <VirtualTable
              height="30rem"
              estimatedRowHeight={46}
              columnExtensions={columns.map((column) => ({
                columnName: column.name,
                align: column.name === "trackingStatus" ? "center" : "right",
              }))}
            />
            <TableColumnResizing
              defaultColumnWidths={columns.map((column) => ({
                columnName: column.name,
                width: column.width,
              }))}
            />
            <TableHeaderRow contentComponent={TableHeaderContent} />
            <TableFixedColumns leftColumns={["orderId"]} />
            <PagingPanel pageSizes={pageSizes} />
          </Grid>
          {loading && (
            <div className="absolute-center">
              <LoadingSlideNoText />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PixelValidationChart() {
  const { user } = useUser();
  const { dateRangeInterval } = usePageConfiguration();

  const {
    data: pixelTrackedOrdersOverTime,
    loading: pixelTrackedOrdersOverTimeLoading,
  } = useNorthbeamQuery<
    PixelTrackedOrdersOverTime,
    PixelTrackedOrdersOverTimeVariables
  >(PIXEL_TRACKED_ORDERS_OVER_TIME_QUERY, {
    variables: {
      filter: {
        dateRange: {
          start: dateRangeInterval.startDate,
          end: dateRangeInterval.endDate,
        },
      },
    },
  });

  const { datasets, labels, totals } = useMemo(() => {
    const data =
      pixelTrackedOrdersOverTime?.me.pixelTrackingEvents
        .pixelTrackedOrdersOverTime?.data?.rows ?? [];
    const pixelTrackedDataset: ChartDataset<"bar", number[]>["data"] = [];
    const pixelTrackableDataset: ChartDataset<"bar", number[]>["data"] = [];
    const dateLabels: ChartData<"bar", ScatterDataPoint[], string>["labels"] =
      [];
    let totalPixelTracked = 0;
    let totalPixelTrackedRev = 0;
    let totalPixelTrackable = 0;
    let totalPixelTrackableRev = 0;

    data.forEach((row) => {
      totalPixelTracked += row.pixelTracked;
      totalPixelTrackedRev += row.pixelTrackedRevenue;
      totalPixelTrackable += row.pixelTrackable;
      totalPixelTrackableRev += row.pixelTrackableRevenue;

      pixelTrackedDataset.push(row.pixelTracked);
      pixelTrackableDataset.push(row.pixelTrackable);
      dateLabels.push(row.occurredAtHourly);
    });

    return {
      totals: {
        pixelTracked: totalPixelTracked,
        pixelTrackedRev: totalPixelTrackedRev,
        pixelTrackable: totalPixelTrackable,
        pixelTrackableRev: totalPixelTrackableRev,
      },
      labels: dateLabels,
      datasets: [
        {
          label: "Pixel Confirmed",
          data: pixelTrackedDataset,
          pointRadius: 3,
          backgroundColor: "#5CBF64",
          borderColor: "#5CBF64",
          yAxisID: "totalY",
        },
        {
          label: "Pixel Trackable",
          data: pixelTrackableDataset,
          pointRadius: 3,
          backgroundColor: "#7081FF",
          borderColor: "#7081FF",
          yAxisID: "totalY",
        },
      ],
    };
  }, [
    pixelTrackedOrdersOverTime?.me.pixelTrackingEvents
      .pixelTrackedOrdersOverTime?.data?.rows,
  ]);

  const { revMetric, txnsMetric, percentBasedMetric } = useMemo(() => {
    const metrics = makeOverviewRows(
      "northbeam_custom",
      "infinity",
      false,
      "cash",
      [],
      "daily",
      user as any,
    );

    const rev = metrics.find((e) => e.metadata?.overviewPageMetricId === "rev");
    const txns = metrics.find(
      (e) => e.metadata?.overviewPageMetricId === "txns",
    );

    const percentBased = metrics.find(
      (e) => e.metadata?.overviewPageMetricId === "ctr",
    );

    return {
      revMetric: rev,
      txnsMetric: txns,
      percentBasedMetric: percentBased,
    } as const;
  }, [user]);

  return (
    <div className="flex">
      <div className="shrink-0 w-[320px]  p-4 bg-cosmic-5 rounded-lg border border-gray-0">
        <a
          href="https://docs.northbeam.io/docs/guide-to-troubleshooting-pixel-tracking#step-1-check-pixel-tracked-percentages"
          className="basis-0 text-md font-semibold  "
          target="_blank"
          rel="noreferrer"
        >
          Step 1: Check Pixel Confirmed Percentages
        </a>
        <ul className="text-xs space-y-5 list-decimal ml-3 mb-0 font-normal mt-5">
          <li>
            Review your Pixel Confirmed percentage
            <ul className="space-y-1 list-[lower-roman] ml-3 mt-2">
              <li>
                A healthy percentage is above 85% (Northbeam allows for a 15%
                gap due to ad blockers, VPNs, etc.)
              </li>
            </ul>
          </li>
          <li>
            <span className="font-semibold">If it’s 85% or higher,</span> you’re
            good — no action needed!
          </li>
          <li>
            <span className="font-semibold">If it’s below 85%,</span> check the
            table of orders to identify which ones aren’t being tracked by the
            Northbeam pixel.
          </li>
        </ul>
      </div>
      <div className="ml-5  w-[calc(100%-320px)] mt-5 gap-y-2 flex flex-col ">
        <DateFilters />
        <div className="mb-4 flex items-stretch gap-4">
          <MetricCard
            title="% Pixel Confirmed Count"
            value={
              totals.pixelTrackable
                ? totals.pixelTracked / totals.pixelTrackable
                : 0
            }
            metric={percentBasedMetric}
            loading={pixelTrackedOrdersOverTimeLoading}
          />
          <MetricCard
            title="% Pixel Confirmed Revenue"
            value={
              totals.pixelTrackableRev
                ? totals.pixelTrackedRev / totals.pixelTrackableRev
                : 0
            }
            metric={percentBasedMetric}
            loading={pixelTrackedOrdersOverTimeLoading}
          />
          {/* <MetricCard
          title="Pixel Tracked Count"
          value={totals.pixelTracked}
          metric={txnsMetric}
          loading={pixelTrackedOrdersOverTimeLoading}
        />
        <MetricCard
          title="Pixel Tracked Revenue"
          value={totals.pixelTrackedRev}
          metric={revMetric}
          loading={pixelTrackedOrdersOverTimeLoading}
          precision="approximate"
        />
        <MetricCard
          title="Pixel Trackable Count"
          value={totals.pixelTrackable}
          metric={txnsMetric}
          loading={pixelTrackedOrdersOverTimeLoading}
        />
        <MetricCard
          title="Pixel Trackable Revenue"
          value={totals.pixelTrackableRev}
          metric={revMetric}
          loading={pixelTrackedOrdersOverTimeLoading}
          precision="approximate"
        /> */}
        </div>
        <div className="h-[20rem] relative">
          {pixelTrackedOrdersOverTimeLoading && (
            <div className="absolute-center w-36">
              <LoadingSlideNoText className="w-auto" />
            </div>
          )}
          <Bar
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: {
                mode: "index",
                intersect: false,
              },
              plugins: {
                legend: { display: true },
              },
              scales: {
                xAxis: {
                  title: {
                    display: true,
                    text: `${user.timezone} Timezone`,
                  },
                  stacked: true,
                  type: "time",
                  time: {
                    tooltipFormat: "ddd D MMM h:mma",
                    unit: "hour",
                  },
                  ticks: {
                    padding: 10,
                    maxRotation: 0,
                    autoSkip: true,
                    font: {
                      family: "Commons Pro",
                      size: 13,
                    },

                    major: {
                      enabled: true,
                    },
                  },
                  grid: {
                    drawOnChartArea: false,
                    drawTicks: false,
                  },
                },
                totalY: {
                  grid: {
                    drawOnChartArea: true,
                    drawTicks: false,
                    drawBorder: false,
                  },
                  type: "linear",
                  position: "left",
                  ticks: {
                    font: {
                      family: "Commons Pro Mono",
                      size: 11,
                    },
                  },
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: "Pixel Tracked/Trackable",
                  },
                },
              },
              layout: {
                padding: {
                  bottom: 36,
                },
              },
            }}
            data={{
              labels,
              datasets: datasets,
            }}
          />
        </div>
      </div>
    </div>
  );
}

const PIXEL_TRACKED_ORDERS_QUERY = gql`
  query PixelTrackedOrders(
    $orderTrackingFilters: PixelTrackedOrdersInput!
    $generalFilters: PixelTrackingEventsFilterInput
  ) {
    me {
      pixelTrackingEvents(filter: $generalFilters) {
        pixelTrackedOrders(filters: $orderTrackingFilters) {
          data {
            rows {
              conversionEventId
              orderNumber
              orderId
              purchasedAt
              customerStage
              subscriptionStage
              platform
              revenue
              orderTags
              customerTags
              exclusionReason
              isAttributed
              isTrackable
              isPixelTracked
              isPixelTrackable
              isServerTracked
            }
            totalCount
          }
          queryMetadata {
            bqLogs {
              query
              queryParameters
              queryTypes
              labels
              jobId
              queryId
              executionTimestamp
            }
          }
        }
      }
    }
  }
`;

const PIXEL_TRACKED_ORDERS_OVER_TIME_QUERY = gql`
  query PixelTrackedOrdersOverTime($filter: PixelTrackingEventsFilterInput) {
    me {
      pixelTrackingEvents(filter: $filter) {
        pixelTrackedOrdersOverTime {
          data {
            rows {
              pixelTracked
              occurredAtHourly
              pixelTrackable
              pixelTrackableRevenue
              pixelTrackedRevenue
            }
          }
          queryMetadata {
            bqLogs {
              query
              queryParameters
              queryTypes
              labels
              jobId
              queryId
              executionTimestamp
            }
          }
        }
      }
    }
  }
`;
